import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { ConfirmDialog, IconButton, Menu } from "@/shared/ui";
import type { Conversation } from "../lib/groups";
import {
  useDeleteConversation,
  useRenameConversation,
  useToggleFavorite,
} from "../hooks/useConversationActions";
import { KebabIcon, PencilIcon, StarIcon, TrashIcon } from "./icons";

export interface ConversationRowProps {
  conversation: Conversation;
  active: boolean;
}

/**
 * One sidebar history row: a link to the thread plus a hover kebab with rename
 * (inline), favorite toggle, and delete (confirmed). The active row takes the
 * ink fill (DESIGN §4.1). All mutations re-sync the list.
 */
export function ConversationRow({ conversation, active }: ConversationRowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rename = useRenameConversation();
  const favorite = useToggleFavorite();
  const remove = useDeleteConversation();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(conversation.title ?? "");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const title = conversation.title?.trim() || t("commandPalette.untitled");

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function startRename() {
    setDraft(conversation.title ?? "");
    setEditing(true);
  }

  function commitRename() {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== conversation.title) {
      rename.mutate({ id: conversation.id, title: next });
    }
  }

  function confirmDelete() {
    remove.mutate(conversation.id, {
      onSuccess: () => {
        setConfirmingDelete(false);
        if (active) void navigate({ to: "/chat" });
      },
    });
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        autoFocus
        aria-label={t("conversations.rename")}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitRename}
        onKeyDown={(event) => {
          if (event.key === "Enter") commitRename();
          else if (event.key === "Escape") setEditing(false);
        }}
        className="w-full rounded-sm bg-bubble px-3 py-1.5 font-sans text-[13.5px] text-ink outline-none focus-visible:bg-tint"
      />
    );
  }

  return (
    <div className="group/row relative flex items-center">
      <Link
        to="/chat/$id"
        params={{ id: conversation.id }}
        className={cn(
          "flex min-w-0 flex-1 items-center rounded-sm py-1.5 pl-3 pr-8 font-sans text-[13.5px] outline-none transition-colors duration-150 ease-house",
          active
            ? "bg-accent-subtle font-medium text-ink"
            : "text-ink hover:bg-line-soft focus-visible:bg-line-soft",
        )}
      >
        <span className="min-w-0 flex-1 truncate">{title}</span>
      </Link>

      <div
        className={cn(
          "absolute right-1 opacity-0 transition-opacity duration-150 ease-house group-focus-within/row:opacity-100 group-hover/row:opacity-100",
          active && "opacity-100",
        )}
      >
        <Menu
          label={t("messages.moreActions")}
          align="end"
          trigger={({ toggle }) => (
            <IconButton
              size="sm"
              aria-label={t("messages.moreActions")}
              onClick={toggle}
              className={cn(
                "mr-px mt-[3px] flex size-6 items-center justify-center rounded-[7px]",
                active && "text-ink-soft hover:bg-ink/10 hover:text-ink",
              )}
            >
              <KebabIcon className="size-4" />
            </IconButton>
          )}
          items={[
            { id: "rename", label: t("conversations.rename"), icon: <PencilIcon />, onSelect: startRename },
            {
              id: "favorite",
              label: conversation.is_favorite
                ? t("conversations.removeFavorite")
                : t("conversations.addToFavorites"),
              icon: <StarIcon />,
              onSelect: () => favorite.mutate(conversation.id),
            },
            {
              id: "delete",
              label: t("conversations.delete"),
              icon: <TrashIcon />,
              separated: true,
              onSelect: () => setConfirmingDelete(true),
            },
          ]}
        />
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title={t("conversations.deleteConversationTitle")}
        message={t("conversations.deleteConversationMessage")}
        confirmLabel={t("conversations.delete")}
        cancelLabel={t("common.cancel")}
        danger
        busy={remove.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    </div>
  );
}
